import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../utils/api';
import { orderAPI } from '../../services/marketplaceAPI';
import { 
  UserIcon, 
  ClockIcon, 
  StarIcon, 
  MapPinIcon
} from '@heroicons/react/24/outline';
import PaymentGateway from '../../components/payments/PaymentGateway';
import './EmergencyNurseMarketplaceSimple.css';

const EmergencyNurseMarketplace = () => {
  const navigate = useNavigate();
  const [nurses, setNurses] = useState([]);
  const [filteredNurses, setFilteredNurses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('browse');
  const [patientRequests, setPatientRequests] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [specializationFilter, setSpecializationFilter] = useState('');
  const [availabilityFilter, setAvailabilityFilter] = useState('available');
  const [selectedNurse, setSelectedNurse] = useState(null);
  const [showBookingDialog, setShowBookingDialog] = useState(false);
  const [bookingData, setBookingData] = useState({
    description: '',
    urgency: 'medium',
    estimatedDuration: '1',
    preferredTime: '',
    location: ''
  });
  const [bookingLoading, setBookingLoading] = useState(false);
  const [bookingSuccess, setBookingSuccess] = useState(false);
  const [showGateway, setShowGateway] = useState(false);
  const [amountToPay, setAmountToPay] = useState(0);
  const [userProfile, setUserProfile] = useState(null);
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem('nurse-marketplace-theme');
    return saved === 'dark' || (!saved && window.matchMedia('(prefers-color-scheme: dark)').matches);
  });

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', isDarkMode ? 'dark' : 'light');
    localStorage.setItem('nurse-marketplace-theme', isDarkMode ? 'dark' : 'light');
  }, [isDarkMode]);

  const toggleDarkMode = () => {
    setIsDarkMode(prev => !prev);
  };

  const specializations = [
    'General', 'ICU', 'Emergency', 'Pediatric', 'Surgical', 
    'Cardiac', 'Oncology', 'Psychiatric', 'Other'
  ];

  const urgencyLevels = [
    { value: 'low', label: 'Low Priority', color: 'green' },
    { value: 'medium', label: 'Medium Priority', color: 'yellow' },
    { value: 'high', label: 'High Priority', color: 'orange' },
    { value: 'critical', label: 'Critical', color: 'red' }
  ];

  useEffect(() => {
    fetchAvailableNurses();
  }, []);

  useEffect(() => {
    if (showGateway || activeTab !== 'browse') return;
    const intervalId = setInterval(() => {
      fetchAvailableNurses();
    }, 30000);
    return () => clearInterval(intervalId);
  }, [showGateway, activeTab]);

  useEffect(() => {
    if (activeTab === 'requests' || activeTab === 'completed') {
      fetchPatientRequests();
      const id = setInterval(() => fetchPatientRequests(), 20000);
      return () => clearInterval(id);
    }
  }, [activeTab]);

  useEffect(() => {
    filterNurses();
  }, [nurses, searchTerm, specializationFilter, availabilityFilter]);

  // Fetch user profile on mount
  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        const profileResponse = await api.get('/patient-profile/profile');
        if (profileResponse.data?.success && profileResponse.data?.data) {
          setUserProfile(profileResponse.data.data);
        } else {
          const authResponse = await api.get('/auth/check-auth');
          if (authResponse.data?.success && authResponse.data?.user) {
            setUserProfile(authResponse.data.user);
          }
        }
      } catch (err) {
        console.error('Error fetching user profile:', err);
        const userData = JSON.parse(localStorage.getItem('user') || 'null');
        if (userData) {
          setUserProfile(userData);
        }
      }
    };
    
    fetchUserProfile();
  }, []);

  const fetchAvailableNurses = async () => {
    try {
      setLoading(true);
      const response = await api.get('/emergency-nurse/available-nurses');
      if (response.data.success) {
        const availableNurses = response.data.data.nurses
          .filter(nurse => nurse.nurseDetails?.isAvailable);
        setNurses(availableNurses);
      }
    } catch (err) {
      setError('Failed to fetch available nurses');
      console.error('Error fetching nurses:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchPatientRequests = async () => {
    try {
      const resp = await api.get('/emergency-nurse/patient/requests');
      if (resp.data?.success) {
        setPatientRequests(resp.data.data.requests || []);
      }
    } catch (e) {
      // silent
    }
  };

  const filterNurses = () => {
    let filtered = nurses;

    if (searchTerm) {
      filtered = filtered.filter(nurse =>
        nurse.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        nurse.nurseDetails?.specialization?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (specializationFilter) {
      filtered = filtered.filter(nurse =>
        nurse.nurseDetails?.specialization === specializationFilter
      );
    }

    if (availabilityFilter === 'online') {
      filtered = filtered.filter(nurse => nurse.isOnline);
    }

    setFilteredNurses(filtered);
  };

  const handleBookNurse = (nurse) => {
    setSelectedNurse(nurse);
    setShowBookingDialog(true);
  };

  const handleBookingSubmit = async () => {
    try {
      setBookingLoading(true);
      
      const hours = Math.max(1, parseInt(bookingData.estimatedDuration) || 1);
      const rateMap = { critical: 800, high: 650, medium: 500, low: 380 };
      const rate = rateMap[bookingData.urgency] || rateMap.medium;
      const amount = rate * hours;
      
      setBookingData(prev => ({ ...prev, amount, nurseId: selectedNurse._id }));
      setAmountToPay(amount);
      setShowBookingDialog(false);
      setShowGateway(true);
    } catch (err) {
      setError('Failed to start checkout. Please try again.');
      console.error('Error starting checkout:', err);
    } finally {
      setBookingLoading(false);
    }
  };

  const getUrgencyColor = (urgency) => {
    const level = urgencyLevels.find(u => u.value === urgency);
    return level ? level.color : 'gray';
  };

  const getUrgencyLabel = (urgency) => {
    const level = urgencyLevels.find(u => u.value === urgency);
    return level ? level.label : 'Unknown';
  };

  if (loading) {
    return (
      <div className={`loading-container ${isDarkMode ? 'dark-mode' : ''}`}>
        <div style={{ textAlign: 'center' }}>
          <div className="loading-spinner"></div>
          <div className="loading-text">Loading available nurses...</div>
        </div>
      </div>
    );
  }

  return (
    <div className={`nurse-marketplace-page ${isDarkMode ? 'dark-mode' : ''}`}>
      {/* Top Right Buttons */}
      <div className="top-right-buttons">
        <button
          onClick={toggleDarkMode}
          className="dark-mode-toggle"
          aria-label={isDarkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
          title={isDarkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
        >
          {isDarkMode ? (
            <svg className="toggle-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
          ) : (
            <svg className="toggle-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
            </svg>
          )}
        </button>
        <button 
          onClick={() => navigate('/emergency')}
          className="back-button-top"
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12.5 15L7.5 10L12.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <span>Back</span>
        </button>
      </div>

      {/* Header */}
      <div className="marketplace-header">
        <div className="header-container">
          <div className="header-content">
            <div className="header-title-section">
              <h1 className="header-title">Emergency Nurse Services</h1>
              <p className="header-subtitle">Find and book qualified nurses for emergency medical assistance</p>
            </div>
          </div>

          {/* Tabs */}
          <div className="tabs-container">
            <button
              onClick={() => setActiveTab('browse')}
              className={`tab-button ${activeTab === 'browse' ? 'active' : ''}`}
            >
              Browse Nurses
            </button>
            <button
              onClick={() => setActiveTab('requests')}
              className={`tab-button ${activeTab === 'requests' ? 'active' : ''}`}
            >
              My Requests
            </button>
            <button
              onClick={() => setActiveTab('completed')}
              className={`tab-button ${activeTab === 'completed' ? 'active' : ''}`}
            >
              Completed
            </button>
          </div>
        </div>
      </div>

      <div className="content-container">
        {activeTab === 'browse' && (
          <>
            {/* Filters */}
            <div className="filters-section">
              <div className="filters-grid">
                <input
                  type="text"
                  placeholder="Search nurses..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="filter-input"
                />
                <select
                  value={specializationFilter}
                  onChange={(e) => setSpecializationFilter(e.target.value)}
                  className="filter-select"
                >
                  <option value="">All Specializations</option>
                  {specializations.map(spec => (
                    <option key={spec} value={spec}>{spec}</option>
                  ))}
                </select>
                <select
                  value={availabilityFilter}
                  onChange={(e) => setAvailabilityFilter(e.target.value)}
                  className="filter-select"
                >
                  <option value="available">All Available</option>
                  <option value="online">Online Now</option>
                </select>
                <button
                  onClick={fetchAvailableNurses}
                  className="refresh-button"
                >
                  Refresh
                </button>
              </div>
            </div>

            {/* Results Count */}
            <div className="results-count">
              {filteredNurses.length} nurse{filteredNurses.length !== 1 ? 's' : ''} available
            </div>

            {/* Error Alert */}
            {error && (
              <div className="alert alert-error">
                <span>{error}</span>
                <button 
                  onClick={() => setError(null)}
                  className="alert-close"
                >
                  ×
                </button>
              </div>
            )}

            {/* Success Alert */}
            {bookingSuccess && (
              <div className="alert alert-success">
                <span>Nurse booking request sent successfully! The nurse will be notified and respond shortly.</span>
                <button 
                  onClick={() => setBookingSuccess(false)}
                  className="alert-close"
                >
                  ×
                </button>
              </div>
            )}

            {/* Nurse Cards Grid */}
            <div className="nurses-grid">
              {filteredNurses.map((nurse) => (
                <div key={nurse._id} className="nurse-card">
                  {/* Header */}
                  <div className="nurse-card-header">
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem', flex: 1 }}>
                      <div className="nurse-avatar">
                        {nurse.name?.charAt(0) || 'N'}
                      </div>
                      <div className="nurse-info">
                        <h3 className="nurse-name">{nurse.name}</h3>
                        <div className="nurse-badges">
                          <span className="nurse-badge badge-specialization">
                            {nurse.nurseDetails?.specialization || 'General'}
                          </span>
                          {nurse.isOnline && (
                            <span className="nurse-badge badge-online">
                              Online
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Rating Section */}
                  <div className="rating-section">
                    <div className="rating-stars">
                      {[...Array(5)].map((_, i) => (
                        <StarIcon
                          key={i}
                          className={`star-icon ${i < Math.round(nurse.rating || 0) ? '' : 'empty'}`}
                        />
                      ))}
                      <span className="rating-text">
                        {(nurse.rating || 0).toFixed(1)} ({nurse.totalPatients || 0} patients)
                      </span>
                    </div>
                    <div className="nurse-stats">
                      <div className="stat-item">
                        <ClockIcon className="stat-icon" />
                        <span>{nurse.nurseDetails?.yearsOfExperience || 0} years exp.</span>
                      </div>
                      <div className="stat-item">
                        <ClockIcon className="stat-icon" />
                        <span>~{nurse.avgResponseTime || 0} min response</span>
                      </div>
                    </div>
                  </div>

                  {/* Shift Info */}
                  <div className="shift-section">
                    <div className="shift-label">Current Shift</div>
                    <span className="shift-badge">
                      {nurse.nurseDetails?.shift || 'morning'}
                    </span>
                  </div>

                  {/* Action Button */}
                  <button
                    onClick={() => handleBookNurse(nurse)}
                    disabled={!nurse.isOnline}
                    className={`nurse-action-button ${nurse.isOnline ? 'active' : 'disabled'}`}
                  >
                    {nurse.isOnline ? 'Book Emergency Service' : 'Currently Offline'}
                  </button>
                </div>
              ))}
            </div>

            {/* No Results */}
            {filteredNurses.length === 0 && !loading && (
              <div className="no-results">
                <UserIcon className="no-results-icon" />
                <h3 className="no-results-title">No nurses found</h3>
                <p className="no-results-text">
                  Try adjusting your search criteria or check back later for available nurses.
                </p>
              </div>
            )}
          </>
        )}

        {activeTab !== 'browse' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {(activeTab === 'requests'
              ? patientRequests.filter(r => r.status==='pending' || r.status==='accepted' || r.status==='in_progress')
              : patientRequests.filter(r => r.status==='completed'))
              .map((request) => (
                <div key={request._id} className="request-card">
                  <div className="request-card-header">
                    <div>
                      <div className="request-nurse-name">{request.nurseId?.name || 'Nurse'}</div>
                      <div className="request-details">
                        {request.nurseId?.nurseDetails?.specialization || 'General'} • {request.urgency}
                      </div>
                      <div className="request-date">
                        Requested: {new Date(request.createdAt).toLocaleString()}
                      </div>
                      {request.status==='completed' && (
                        <div className="request-date">
                          Completed: {new Date(request.completedAt).toLocaleString()}
                        </div>
                      )}
                    </div>
                    <div>
                      <span className={`request-status status-${request.status.replace('_', '-')}`}>
                        {request.status.replace('_', ' ')}
                      </span>
                    </div>
                  </div>
                </div>
              ))}

            {(activeTab === 'requests' && patientRequests.filter(r => r.status==='pending' || r.status==='accepted' || r.status==='in_progress').length===0) && (
              <div className="no-results">
                <UserIcon className="no-results-icon" />
                <h3 className="no-results-title">No active requests</h3>
              </div>
            )}

            {(activeTab === 'completed' && patientRequests.filter(r => r.status==='completed').length===0) && (
              <div className="no-results">
                <UserIcon className="no-results-icon" />
                <h3 className="no-results-title">No completed requests</h3>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Booking Dialog */}
      {showBookingDialog && selectedNurse && (
        <div className="booking-dialog-overlay" onClick={() => setShowBookingDialog(false)}>
          <div className="booking-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="dialog-header">
              <div className="dialog-nurse-avatar">
                {selectedNurse?.name?.charAt(0) || 'N'}
              </div>
              <div>
                <h3 className="dialog-title">Book {selectedNurse?.name}</h3>
                <p className="dialog-subtitle">
                  {selectedNurse?.nurseDetails?.specialization} Nurse
                </p>
              </div>
            </div>

            <div className="dialog-form">
              <div className="form-group">
                <label className="form-label">Emergency Description *</label>
                <textarea
                  placeholder="Describe the emergency situation..."
                  value={bookingData.description}
                  onChange={(e) => setBookingData(prev => ({ ...prev, description: e.target.value }))}
                  rows={3}
                  required
                  className="form-textarea"
                />
              </div>

              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">Urgency Level *</label>
                  <select
                    value={bookingData.urgency}
                    onChange={(e) => setBookingData(prev => ({ ...prev, urgency: e.target.value }))}
                    className="form-select"
                  >
                    {urgencyLevels.map(level => (
                      <option key={level.value} value={level.value}>
                        {level.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Estimated Duration (hours) *</label>
                  <select
                    value={bookingData.estimatedDuration}
                    onChange={(e) => setBookingData(prev => ({ ...prev, estimatedDuration: e.target.value }))}
                    className="form-select"
                  >
                    <option value="1">1 hour</option>
                    <option value="2">2 hours</option>
                    <option value="4">4 hours</option>
                    <option value="8">8 hours</option>
                    <option value="12">12 hours</option>
                  </select>
                </div>
              </div>

              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">Preferred Time</label>
                  <input
                    type="datetime-local"
                    value={bookingData.preferredTime}
                    onChange={(e) => setBookingData(prev => ({ ...prev, preferredTime: e.target.value }))}
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Location</label>
                  <input
                    type="text"
                    placeholder="Patient location"
                    value={bookingData.location}
                    onChange={(e) => setBookingData(prev => ({ ...prev, location: e.target.value }))}
                    className="form-input"
                  />
                </div>
              </div>

              {/* Urgency Indicator */}
              <div className="urgency-indicator">
                <div className={`urgency-dot ${getUrgencyColor(bookingData.urgency)}`}></div>
                <span>
                  {getUrgencyLabel(bookingData.urgency)} - {bookingData.urgency === 'critical' ? 'Immediate response required' : 'Standard response time'}
                </span>
              </div>
            </div>

            <div className="dialog-actions">
              <button
                onClick={() => setShowBookingDialog(false)}
                className="dialog-button dialog-button-cancel"
              >
                Cancel
              </button>
              <button
                onClick={handleBookingSubmit}
                disabled={bookingLoading || !bookingData.description}
                className="dialog-button dialog-button-submit"
              >
                {bookingLoading ? 'Sending Request...' : 'Send Emergency Request'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Shared Payment Gateway */}
      {showGateway && (
        <PaymentGateway
          open={showGateway}
          onClose={() => setShowGateway(false)}
          amount={amountToPay}
          paymentType="emergency_nurse"
          onSuccess={async (paymentData) => {
            try {
              console.log('Payment successful for emergency nurse:', paymentData);
              
              const locationString = bookingData.location || '';
              const addressParts = locationString.split(',').map(part => part.trim());
              
              const shippingAddress = {
                street: addressParts[0] || locationString || 'Emergency Location',
                city: addressParts[1] || 'Dhaka',
                state: addressParts[2] || 'Dhaka',
                zipCode: addressParts[3] || '1000',
                country: 'Bangladesh'
              };
              
              const customerName = userProfile?.name || userProfile?.patientDetails?.name || 'Emergency Patient';
              const customerPhone = userProfile?.phone || userProfile?.patientDetails?.phone || 'Emergency Contact';
              
              const orderPayload = {
                orderId: paymentData.orderId,
                amount: paymentData.amount,
                paymentMethod: paymentData.paymentMethod,
                paymentType: 'emergency_nurse',
                transactionId: paymentData.transactionId,
                shippingAddress: shippingAddress,
                items: [{
                  type: 'emergency_nurse',
                  nurseId: bookingData.nurseId,
                  description: bookingData.description,
                  urgency: bookingData.urgency,
                  estimatedDuration: parseInt(bookingData.estimatedDuration) || 1,
                  preferredTime: bookingData.preferredTime,
                  location: bookingData.location,
                  quantity: 1,
                  price: bookingData.amount
                }],
                customerInfo: {
                  name: customerName,
                  phone: customerPhone
                },
                deliveryInfo: {
                  address: bookingData.location,
                  type: 'emergency_service'
                }
              };
              
              console.log('Creating order after payment (marketplace approach):', orderPayload);
              const response = await orderAPI.createOrderAfterPayment(orderPayload);
              
              if (response.success) {
                setShowGateway(false);
                setBookingSuccess(true);
                
                setTimeout(() => {
                  navigate('/orders');
                }, 2000);
              } else {
                throw new Error(response.message || 'Failed to create order');
              }
            } catch (e) {
              console.error('Error creating order:', e);
              setError(e?.response?.data?.message || 'Failed to create order');
              setShowGateway(false);
            }
          }}
          userProfile={userProfile}
        />
      )}
    </div>
  );
};

export default EmergencyNurseMarketplace;
